import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Mail, FileText, Send, Building2 } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function Contact() {
    const { t, lang } = useLang();
    const c = t.contactPage;

    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
    const [loading, setLoading] = useState(false);
    const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.sendContact({ ...form, language: lang });
            toast.success(c.success);
            setForm({ name: "", email: "", subject: "", message: "" });
        } catch (err) {
            toast.error(c.error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div data-testid="page-contact" className="px-6 md:px-10 py-12 md:py-20">
            <div className="max-w-7xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="max-w-3xl"
                >
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-caireen-blue/10 text-caireen-blue font-sans text-xs tracking-widest uppercase">
                        <Mail className="w-3.5 h-3.5" />
                        {t.nav.contact}
                    </span>
                    <h1 className="mt-5 font-serif text-5xl md:text-6xl tracking-tight leading-[1.05]">{c.title}</h1>
                    <p className="mt-5 font-sans text-lg text-caireen-ink/70">{c.sub}</p>
                </motion.div>

                <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <motion.form
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        onSubmit={onSubmit}
                        className="lg:col-span-7 bg-white rounded-3xl ring-1 ring-caireen-ink/10 p-7 md:p-10 space-y-5"
                        data-testid="contact-form"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="font-sans text-sm">{c.name}</Label>
                                <Input data-testid="contact-name" required value={form.name} onChange={onChange("name")} className="rounded-xl bg-caireen-cream/50 border-caireen-ink/15 h-12" />
                            </div>
                            <div className="space-y-2">
                                <Label className="font-sans text-sm">{c.email}</Label>
                                <Input data-testid="contact-email" required type="email" value={form.email} onChange={onChange("email")} className="rounded-xl bg-caireen-cream/50 border-caireen-ink/15 h-12" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label className="font-sans text-sm">{c.subject}</Label>
                            <Input data-testid="contact-subject" value={form.subject} onChange={onChange("subject")} className="rounded-xl bg-caireen-cream/50 border-caireen-ink/15 h-12" />
                        </div>
                        <div className="space-y-2">
                            <Label className="font-sans text-sm">{c.message}</Label>
                            <Textarea data-testid="contact-message" required rows={6} value={form.message} onChange={onChange("message")} className="rounded-xl bg-caireen-cream/50 border-caireen-ink/15" />
                        </div>
                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="contact-submit"
                            className="rounded-full bg-caireen-orange hover:bg-caireen-red text-white font-semibold py-6 px-8 text-base"
                        >
                            <Send className="w-4 h-4 mr-2" />
                            {c.submit}
                        </Button>
                    </motion.form>

                    <motion.aside
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="lg:col-span-5 space-y-5"
                    >
                        <div className="bg-white rounded-3xl ring-1 ring-caireen-ink/10 p-7 space-y-5">
                            <div className="flex items-start gap-4">
                                <span className="w-11 h-11 rounded-2xl bg-caireen-olive/15 text-caireen-olive flex items-center justify-center shrink-0">
                                    <Building2 className="w-5 h-5" strokeWidth={1.75} />
                                </span>
                                <div>
                                    <p className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">{c.org}</p>
                                    <p className="mt-1 font-serif text-lg">Caireen Verein (Caireen e.V.)</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <span className="w-11 h-11 rounded-2xl bg-caireen-orange/10 text-caireen-orange flex items-center justify-center shrink-0">
                                    <MapPin className="w-5 h-5" strokeWidth={1.75} />
                                </span>
                                <div>
                                    <p className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">{c.address}</p>
                                    <p className="mt-1 font-serif text-lg">Innerteuchen 26<br />9543 Arriach, Österreich</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <span className="w-11 h-11 rounded-2xl bg-caireen-blue/10 text-caireen-blue flex items-center justify-center shrink-0">
                                    <FileText className="w-5 h-5" strokeWidth={1.75} />
                                </span>
                                <div>
                                    <p className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">{c.zvr}</p>
                                    <p className="mt-1 font-serif text-lg">1121813363</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <span className="w-11 h-11 rounded-2xl bg-caireen-red/10 text-caireen-red flex items-center justify-center shrink-0">
                                    <Mail className="w-5 h-5" strokeWidth={1.75} />
                                </span>
                                <div>
                                    <p className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">Email</p>
                                    <a href="mailto:office@caireen.at" className="mt-1 font-serif text-lg text-caireen-ink hover:text-caireen-orange transition-colors block">
                                        office@caireen.at
                                    </a>
                                </div>
                            </div>
                        </div>

                        <div className="rounded-3xl overflow-hidden ring-1 ring-caireen-ink/10 aspect-[4/3]">
                            <iframe
                                title="Caireen Verein – Arriach"
                                src="https://www.openstreetmap.org/export/embed.html?bbox=13.84%2C46.69%2C13.92%2C46.74&amp;layer=mapnik&amp;marker=46.715%2C13.88"
                                className="w-full h-full"
                                loading="lazy"
                            />
                        </div>
                    </motion.aside>
                </div>
            </div>
        </div>
    );
}
